import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Funnel,
  FileText,
  Scale,
  Building2,
  ClipboardList,
  Tags,
  Calculator,

  FolderOpen,
  Settings as SettingsIcon,
  LogOut,
  Moon,
  Sun,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronRight,
  UserRound,
  ScanSearch,
  PackageCheck,
  FolderKanban,
  CalendarDays,
  Truck,
  Map as MapIcon,
  Warehouse,
  Wrench,
  ShoppingCart,
  Receipt,
  Landmark,
  PiggyBank,
  ClipboardCheck,
  Clock,
  Inbox,
  PenLine, DraftingCompass } from "lucide-react";

// Field Operations sub-tabs (1:1 with the ported FieldOps nav; routed by URL).
const FIELDOPS_SUBTABS = [
  { key: "fo-dashboard", to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "field.dashboard" },
  // Resource Allocation — the field crew's job list.
  { key: "fo-projects", to: "/projects", label: "RA", icon: FolderOpen, perm: "field.ra" },
  { key: "fo-schedule", to: "/schedule", label: "Schedule", icon: CalendarDays, perm: "field.ra" },
  { key: "fo-dispatch", to: "/dispatch", label: "Dispatch", icon: Truck, perm: "field.ra" },
  { key: "fo-map", to: "/map", label: "Map", icon: MapIcon, perm: "field.ra" },
  { key: "fo-install", to: "/install", label: "Install", icon: Wrench, perm: "field.install" },
  { key: "fo-drawings", to: "/drawings", label: "Drawings", icon: PenLine, perm: "operations.drawings" },
  { key: "fo-warehouse", to: "/warehouse", label: "Warehouse", icon: Warehouse, perm: "field.warehouse" },
  { key: "fo-timeclock", to: "/timeclock", label: "Time Clock", icon: Clock, perm: "field.time" },
  { key: "fo-timesheets", to: "/timesheets", label: "Timesheets", icon: ClipboardCheck, perm: "field.time" },
];

const GROUPS = [
  {
    label: "Sales",
    area: "sales",
    items: [
      { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "sales.dashboard" },
      { key: "bod", label: "Basis of Design", icon: DraftingCompass, perm: "estimating.budgets" },
      { key: "pipeline", label: "Pipeline", icon: Funnel, perm: "sales.pipeline" },
      { key: "contracts", label: "Contracts", icon: FileText, perm: "sales.contracts" },
      { key: "contractadmin", label: "Contract Admin", icon: ScanSearch, perm: "sales.contract_admin" },
      { key: "reconcile", label: "Reconcile", icon: Scale, perm: "sales.reconcile" },
      { key: "accounts", label: "Accounts", icon: Building2, perm: "sales.accounts" },
    ],
  },
  {
    label: "Estimating",
    area: "estimating",
    items: [
      { key: "itbinbox", label: "Inbox", icon: Inbox, perm: "estimating.bid_board" },
      { key: "bidboard", label: "Bid Board", icon: ClipboardList, perm: "estimating.bid_board" },
      { key: "budget", label: "Budgets", icon: Calculator, perm: "estimating.budgets" },
      { key: "pricelist", label: "Cost Library", icon: Tags, perm: "estimating.cost_library" },
      { key: "doclibrary", label: "Doc Library", icon: FolderOpen, perm: "estimating.doc_library" },
    ],
  },
  {
    label: "Operations",
    area: "operations",
    items: [
      { key: "opsdashboard", label: "Dashboard", icon: LayoutDashboard, perm: "operations.dashboard" },
      { key: "opsprojects", label: "Jobs", icon: FolderKanban, perm: "operations.projects" },
      { key: "subbuilder", label: "SubBuilder", icon: PackageCheck, perm: "operations.subbuilder" },
      { key: "purchasing", label: "Purchasing", icon: ShoppingCart, perm: "operations.purchasing" },
      { key: "billing", label: "Billing", icon: Receipt, perm: "operations.invoicing" },
      { key: "ar", label: "Receivables", icon: Landmark, perm: "operations.invoicing" },
      { key: "retention", label: "Retention", icon: PiggyBank, perm: "operations.invoicing" },
      { key: "closeout", label: "Closeout", icon: ClipboardCheck, perm: "operations.invoicing" },
    ],
  },
  {
    label: "Field Operations",
    area: "field",
    items: FIELDOPS_SUBTABS,
  },
];

function NavItem({ item, active, collapsed, onClick, compact }) {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={`group relative w-full flex items-center gap-3 rounded-md text-sm transition-colors ${
        collapsed ? "justify-center px-0 py-2.5" : compact ? "px-3 py-1.5" : "px-3 py-2"
      } ${!collapsed && item.nested ? "pl-6 gap-1.5 text-[12px]" : ""} ${
        active
          ? "bg-white/[0.06] text-white font-semibold"
          : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-100 font-medium"
      }`}
      data-testid={`nav-tab-${item.key}`}
    >
      {active && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r bg-blue-500" />
      )}
      <Icon className={`${item.nested && !collapsed ? "h-4 w-4" : "h-[18px] w-[18px]"} shrink-0 ${active ? "text-blue-400" : ""}`} />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </button>
  );
}

function AccountMenu({ user, logout, onTabChange, triggerClassName, roleLabel }) {
  const initials = (user?.email || "")
    .split("@")[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map((s) => s[0].toUpperCase())
    .join("");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`rounded-full hover:opacity-90 transition-opacity ${triggerClassName || ""}`}
          data-testid="account-menu-trigger"
          title={user?.email}
          aria-label="Account menu"
        >
          <span className="h-9 w-9 rounded-full bg-white/[0.08] flex items-center justify-center text-xs font-bold text-slate-200">
            {initials || "U"}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start" className="w-56" data-testid="account-menu">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold truncate">{user?.name || "User"}</span>
            <span className="text-xs text-muted-foreground truncate" data-testid="user-email">{user?.email}</span>
            {roleLabel && (
              <span
                className="mt-1 w-fit rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wide"
                data-testid="account-menu-role"
              >
                {roleLabel}
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onTabChange("settings")} data-testid="account-menu-profile">
          <UserRound className="mr-2 h-4 w-4" />
          My profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} data-testid="logout-btn">
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ⌘ on Mac, Ctrl elsewhere — the palette opens with either.
const MOD_KEY = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "") ? "⌘" : "Ctrl ";

export default function Sidebar({
  activeTab,
  onTabChange,
  theme,
  onToggleTheme,
  collapsed,
  onToggleCollapsed,
  onOpenSearch,
  onGoHome,
  mobileOpen = false,
}) {
  const { user, logout, access, isAdmin, roleLabel, liveTabs } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // LIVE_TABS (production release allow-list): only the listed permission cells are shown.
  const visibleGroups = GROUPS
    .filter((g) => (access?.[g.area] || "none") !== "none")
    .map((g) => ({ ...g, items: g.items.filter((it) => !liveTabs || liveTabs.includes(it.perm)) }))
    .filter((g) => g.items.length > 0);

  const [closedGroups, setClosedGroups] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("abs-closed-groups") || "[]");
    } catch {
      return [];
    }
  });
  const toggleGroup = (label) => {
    setClosedGroups((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  useEffect(() => {
    localStorage.setItem("abs-closed-groups", JSON.stringify(closedGroups));
  }, [closedGroups]);

  // No counters in the left pane — indicators belong on a dashboard, not the navigation.

  return (
    <aside
      className={`abs-sidebar h-screen shrink-0 flex flex-col border-r border-[#262626] transition-[width,transform] duration-200
        fixed inset-y-0 left-0 z-50 md:static md:translate-x-0 ${
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      } ${collapsed ? "w-[64px]" : "w-[188px]"}`}
      style={{ backgroundColor: "#141414" }}
      data-testid="app-sidebar"
    >
      {/* Logo + account + collapse toggle */}
      <div className="relative flex items-center h-16 shrink-0 border-b border-[#262626] px-3">
        {!collapsed && user && (
          <AccountMenu
            user={user}
            logout={logout}
            onTabChange={onTabChange}
            roleLabel={roleLabel}
            triggerClassName="absolute left-2 top-1/2 -translate-y-1/2"
          />
        )}
        {!collapsed && (
          <button type="button" onClick={onGoHome} title="Home"
                  className="flex w-fit flex-col items-stretch gap-0.5 mx-auto translate-x-2 transition-opacity hover:opacity-80"
                  data-testid="sidebar-home-btn">
            <img
              src="/abs-logo.png"
              alt="ABS"
              className="h-9 w-auto invert opacity-90"
              data-testid="sidebar-logo"
            />
            {/* Letters are spread edge to edge so the word matches the logo's width exactly. */}
            <span
              className="flex w-full justify-between text-[10px] font-medium uppercase text-slate-500 leading-none"
              data-testid="sidebar-app-label"
              aria-label="Contracts"
            >
              {"CONTRACTS".split("").map((ch, i) => (
                <span key={i} aria-hidden="true">{ch}</span>
              ))}
            </span>
          </button>
        )}
        <button
          onClick={onToggleCollapsed}
          className={`p-1.5 rounded-md text-slate-400 hover:bg-white/[0.06] hover:text-white transition-colors ${
            collapsed ? "mx-auto" : "absolute right-2 top-1/2 -translate-y-1/2"
          }`}
          data-testid="sidebar-collapse-toggle"
          title={collapsed ? "Expand" : "Collapse"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <PanelLeftOpen className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
        </button>
      </div>

      {/* Account avatar in the rail when collapsed (keeps sign-out reachable) */}
      {collapsed && user && (
        <div className="shrink-0 flex justify-center pt-2">
          <AccountMenu user={user} logout={logout} onTabChange={onTabChange} roleLabel={roleLabel} />
        </div>
      )}

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-5" data-testid="sidebar-nav">
        {visibleGroups.map((group) => {
          const isClosed = !collapsed && closedGroups.includes(group.label);
          return (
            <div key={group.label} className="space-y-1">
              {!collapsed && (
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between px-3 pb-1 group/label"
                  data-testid={`sidebar-group-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
                  aria-expanded={!isClosed}
                >
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[#0076B6]">
                    {group.label}
                  </span>
                  {isClosed ? (
                    <ChevronRight className="h-3 w-3 text-[#0076B6] opacity-0 group-hover/label:opacity-100 transition-opacity" />
                  ) : (
                    <ChevronDown className="h-3 w-3 text-[#0076B6] opacity-0 group-hover/label:opacity-100 transition-opacity" />
                  )}
                </button>
              )}
              {!isClosed &&
                group.items.map((item) => {
                  // Field Operations sub-tabs are URL-routed; the rest are host tabs.
                  if (item.to) {
                    return (
                      <NavItem
                        key={item.key}
                        item={item}
                        active={
                          activeTab === "fieldops" &&
                          (location.pathname === item.to || location.pathname.startsWith(item.to + "/"))
                        }
                        collapsed={collapsed}
                        onClick={() => {
                          onTabChange("fieldops");
                          navigate(item.to);
                        }}
                      />
                    );
                  }
                  return (
                    <NavItem
                      key={item.key}
                      item={item}
                      active={activeTab === item.key}
                      collapsed={collapsed}
                      onClick={() => onTabChange(item.key)}
                    />
                  );
                })}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="shrink-0 border-t border-[#262626] p-2 space-y-0.5">
        <button
          onClick={onOpenSearch}
          title={collapsed ? `Quick Search (${MOD_KEY}K)` : undefined}
          className={`w-full flex items-center gap-3 rounded-md text-sm text-slate-400 hover:bg-white/[0.04] hover:text-slate-100 transition-colors ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-1.5"}`}
          data-testid="sidebar-search-btn"
        >
          {/* The shortcut itself is the icon */}
          <kbd className="-mx-[2px] inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded border border-[#3a3a3a] px-0 font-mono text-[9px] tracking-tighter leading-none text-slate-300"
               data-testid="sidebar-search-kbd">{MOD_KEY}K</kbd>
          {!collapsed && <span className="font-medium whitespace-nowrap">Quick Search</span>}
        </button>

        <button
          onClick={onToggleTheme}
          title={collapsed ? (theme === "dark" ? "Light mode" : "Dark mode") : undefined}
          className={`w-full flex items-center gap-3 rounded-md text-sm text-slate-400 hover:bg-white/[0.04] hover:text-slate-100 transition-colors ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-1.5"}`}
          data-testid="theme-toggle-btn"
        >
          {theme === "dark" ? <Sun className="h-[18px] w-[18px] shrink-0" /> : <Moon className="h-[18px] w-[18px] shrink-0" />}
          {!collapsed && <span className="font-medium">{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>}
        </button>

        {isAdmin && (
          <NavItem
            item={{ key: "settings", label: "Settings", icon: SettingsIcon }}
            active={activeTab === "settings"}
            collapsed={collapsed}
            compact
            onClick={() => onTabChange("settings")}
          />
        )}
      </div>
    </aside>
  );
}
